<template>
  <!-- Ajuste avisos 3D (ajuste_avisos_3d.md, opción A): toast propio y siempre montado para los avisos de la 3D.
       El <Toast /> del POS no existe mientras carga y App.vue no tiene uno global: sin esto, el aviso se perdía. -->
  <Toast group="avisos-3d" />
  <div v-if="conexion.modoOffline" data-testid="banner-offline" class="zapi-banner-offline flex align-items-center justify-content-center surface-100 border-bottom-1 border-red-500 text-red-500">
    <span class="zapi-banner-offline-texto">
      <i class="pi pi-exclamation-triangle mr-2"></i><span class="zapi-banner-offline-titulo font-bold">Modo Offline</span> · En esta sucursal debe vender una sola caja mientras no haya conexión<span v-if="pantallaBloqueada" class="font-bold"> · Esta pantalla no está disponible sin conexión</span>
    </span>
    <div class="zapi-banner-offline-acciones flex align-items-center gap-3">
      <!-- Etapa 3D (A): en rojo y con ícono si hay ventas con más de 60 h. Antes:
      <span v-if="conexion.ventasEnCola > 0" class="font-bold">{{ conexion.ventasEnCola }} en cola</span> -->
      <span v-if="conexion.ventasEnCola > 0" class="font-bold" :class="{ 'text-red-600': conexion.ventasAntiguas > 0 }"
            v-tooltip.bottom="conexion.ventasAntiguas > 0 ? `${conexion.ventasAntiguas} con más de 60 h: el servidor las rechaza a las 72 h` : null">
        <i v-if="conexion.ventasAntiguas > 0" class="pi pi-history mr-1"></i>{{ conexion.ventasEnCola }} en cola</span>
      <!-- Etapa 4 (d): cierre de caja guardado en este equipo, sin enviar -->
      <span v-if="conexion.cierresPendientes > 0" class="font-bold" data-testid="banner-cierre-pendiente"
            v-tooltip.bottom="`Cierre de la caja #${conexion.cajasConCierrePendiente.join(', #')} pendiente de enviar`">
        <i class="pi pi-lock mr-1"></i>Cierre pendiente</span>
      <span v-if="conexion.verificando" class="text-orange-500 font-bold"><i class="pi pi-spin pi-spinner mr-1"></i>Buscando...</span>
      <span v-else class="font-bold text-underline cursor-pointer" @click="conexion.forzarPing()">Reintentar</span>
    </div>
  </div>
  <!-- Etapa 6: versión nueva del service worker esperando (solo con red; sin red manda el aviso offline) -->
  <div v-else-if="conexion.versionNueva" class="zapi-banner-offline flex align-items-center justify-content-center surface-100 border-bottom-1 border-blue-500 text-blue-600">
    <span class="zapi-banner-offline-texto">
      <i class="pi pi-refresh mr-2"></i><span class="zapi-banner-offline-titulo font-bold">Hay una versión nueva del sistema</span>
    </span>
    <div class="zapi-banner-offline-acciones flex align-items-center gap-3">
      <span class="font-bold text-underline cursor-pointer" @click="actualizar">Actualizar</span>
    </div>
  </div>
</template>

<script>
import { useConexion } from '@/stores';
import { MENSAJE_BLOQUEO_OFFLINE, AVISOS } from '@/config/offline';
import { aplicarVersionNueva } from '@/registrarServiceWorker';

export default {
  name: 'ZapiBannerOffline',
  data() {
    return {
      conexion: useConexion()
    };
  },
  computed: {
    // La pantalla actual está en la lista de rutas bloqueadas sin conexión (meta.bloqueadoOffline)
    pantallaBloqueada() {
      return this.$route.matched.some(r => r.meta.bloqueadoOffline);
    },
    // Hay franja visible: aviso offline o aviso de versión nueva
    franjaVisible() {
      return this.conexion.modoOffline || this.conexion.versionNueva;
    },
    // Etapa 3D (B): reloj del equipo con más de 2 min de diferencia con el servidor
    relojDesfasado() {
      const d = this.conexion.desfaseVigente();
      return d !== null && Math.abs(d) > AVISOS.desfaseRelojMaxMs;
    }
  },
  watch: {
    // body.modo-offline baja la barra superior, el menú, el contenido y el POS la altura de la franja
    // (también con el aviso de versión nueva, que ocupa el mismo lugar)
    franjaVisible: {
      immediate: true,
      handler(visible) {
        document.body.classList.toggle('modo-offline', visible);
      }
    },
    // El guard de rutas no tiene acceso a $toast: avisa a través del store
    'conexion.navegacionesBloqueadas'() {
      // Ítem 12: con group 'avisos-3d' (el toast siempre montado del banner). Sin group se perdía en las pantallas
      // que no tienen un <Toast> propio, p. ej. el inicio (alertas_stock_resultado.md). Antes:
      // this.$toast.add({ severity: 'warn', summary: 'Modo Offline', detail: MENSAJE_BLOQUEO_OFFLINE, life: 3000 });
      this.$toast.add({ group: 'avisos-3d', severity: 'warn', summary: 'Modo Offline', detail: MENSAJE_BLOQUEO_OFFLINE, life: 3000 });
    },
    // Etapa 3D (A): aviso único y fijo la primera vez que aparecen ventas antiguas (en cualquier pantalla)
    'conexion.ventasAntiguas'(n, antes) {
      // Ajuste avisos 3D: con group 'avisos-3d' (el toast del banner). Antes, sin group (se perdía mientras cargaba el POS):
      // if (n > 0 && !antes) this.$toast.add({ severity: 'error', summary: 'Ventas sin enviar hace más de 60 h',
      //   detail: 'El servidor las rechaza a las 72 h. Abra «Ventas» en el POS para ver qué hacer.' });
      if (n > 0 && !antes) this.$toast.add({ group: 'avisos-3d', severity: 'error', summary: 'Ventas sin enviar hace más de 60 h',
        detail: 'El servidor las rechaza a las 72 h. Abra «Ventas» en el POS para ver qué hacer.' });
    },
    // Etapa 3D (B): aviso único la primera vez que se detecta el reloj desfasado
    relojDesfasado(desfasado, antes) {
      if (desfasado && !antes) {
        const d = this.conexion.desfaseVigente() || 0;
        // Ajuste avisos 3D: con group 'avisos-3d'. Antes: this.$toast.add({ severity: 'error', summary: …  (sin group)
        this.$toast.add({ group: 'avisos-3d', severity: 'error', summary: 'Hora del equipo incorrecta',
          detail: `La hora está ${d > 0 ? 'adelantada' : 'atrasada'} ${Math.round(Math.abs(d) / 60000)} min: corríjala (Configuración de Windows › Hora › Sincronizar ahora).` });
      }
    }
  },
  methods: {
    // D3: recarga la página con la versión nueva; con productos en el carrito, pregunta antes
    actualizar() {
      if (this.conexion.carritoConProductos
        && !window.confirm('Se recargará la página; el carrito actual se perderá. Las ventas ya cobradas no se pierden. ¿Actualizar ahora?')) return;
      aplicarVersionNueva();
    }
  },
  beforeUnmount() {
    // Al cerrar sesión el layout se desmonta: no dejar el body desplazado en el login
    document.body.classList.remove('modo-offline');
  }
};
</script>

<style scoped>
.zapi-banner-offline {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--altura-banner-offline);
  z-index: 998; /* encima de la barra superior (997) y del POS; debajo de diálogos y toasts */
  padding: 0 1rem;
  white-space: nowrap;
  /* En px: html está a 12px, así que text-base (1rem) daría 12px y no 14px */
  font-size: 14px;
}
.zapi-banner-offline-titulo {
  font-size: 15px;
}
.zapi-banner-offline-texto {
  /* centrado en la franja; deja libre a cada lado el ancho de las acciones (≈ 210px con la letra de 14px) */
  /* Etapa 3D: antes calc(100% - 36rem); «N en cola» puede llevar el ícono de ventas antiguas */
  max-width: calc(100% - 38rem);
  overflow: hidden;
  text-overflow: ellipsis;
}
.zapi-banner-offline-acciones {
  position: absolute;
  top: 0;
  right: 1rem;
  height: 100%;
}
</style>
